import { memo, useEffect, useRef, useState } from 'react';
import { FeatureGroup, LayerGroup, LayersControl, useMap } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';

import classnames from 'classnames';
import L from 'leaflet';
import { OpenStreetMapProvider } from 'leaflet-geosearch';
import debounce from 'lodash.debounce';
import isEmpty from 'lodash.isempty';

import { ConditionalWrapper } from '../../helpers/map';

import { GEOMETRY_TYPE } from '../../constants/map';

import useFeatures from '../../components/Maps/useFeatures';

import './styles.scss';

import BaseLayers from './BaseLayers';
import AppMapContainer from './Container';
import CustomLayers from './CustomLayers';
import EditButtons from './EditButtons';
import {
  EDIT_CONTROL_DRAW_OPTIONS,
  EDIT_CONTROL_POSITION,
  LAYERS_CONTROL_POSITION,
  SHAPE_OPTIONS,
  TYPE,
  ZOOM,
  ZOOM_MAX,
  customizeButtonLabels
} from './constants';

const { Overlay } = LayersControl;

const provider = new OpenStreetMapProvider();

function isValidPosition(position) {
  return (
    Array.isArray(position) &&
    position.length === 2 &&
    position.every(coordinate => typeof coordinate === 'number' && !Number.isNaN(coordinate))
  );
}

function FarmLayer(props) {
  const map = useMap();

  const featureGroupRef = useRef(null);

  const [isFullScreen, setIsFullScreen] = useState(false);
  const [state, setState] = useState({
    color: props.color,
    enableDraw: !!props.isNew,
    farm: props.farm,
    geofences: { features: [] },
    masterGeofences: { features: [] },
    position: [-29.132312, 26.057016],
    sites: { features: [] }
  });

  const initializeFeatureGroup = () => {
    if (!featureGroupRef?.current) return;

    const fg = featureGroupRef.current;

    fg.clearLayers();

    if (isEmpty(props.farm?.master_geofence?.geo_json)) return;

    props.farm?.master_geofence?.geo_json?.properties?.radius
      ? drawCircle(
          fg,
          props.farm?.master_geofence?.geo_json?.geometry.coordinates,
          props.farm?.master_geofence?.geo_json?.properties.radius
        )
      : drawPolygon(fg, props.farm?.master_geofence?.geo_json);
  };

  const handleFullScreenChange = () => {
    setIsFullScreen(!!document.fullscreenElement);
  };

  const clickEditButton = () => {
    if (!props.editing) return;

    const editButton = document.querySelector('a.leaflet-draw-edit-edit');

    if (editButton) editButton.click();
  };

  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullScreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
    };
  }, []);

  useEffect(() => {
    if (props.farm) {
      customizeButtonLabels();
      initializeFeatureGroup();
      tick();
    }
  }, [props.farm]);

  const debounceSearch = debounce(cb => {
    cb();
  }, 500);

  useEffect(() => {
    if (props.defaultPosition?.latitude) {
      if (map) {
        map?.setView([props.defaultPosition?.latitude, props.defaultPosition?.longitude], ZOOM);
      }
    } else {
      debounceSearch(searchPositionByAddress);
    }
  }, [props.defaultPosition]);

  useEffect(() => {
    toggleEditMode();
    clickEditButton();
  }, [props.editing]);

  useEffect(() => {
    handleColorChange();
  }, [props.color]);

  function drawCircle(fg, coordinates, radius) {
    const [lat, lng] = [coordinates?.at(1), coordinates?.at(0)];
    const layer = L.circle([lat, lng], {
      ...SHAPE_OPTIONS,
      color: state.color,
      fillColor: state.color,
      radius
    });

    fg.addLayer(layer);
  }

  function drawPolygon(fg, geoJson) {
    L.geoJSON(geoJson, {
      style: {
        ...SHAPE_OPTIONS,
        color: state.color,
        fillColor: state.color
      }
    }).eachLayer(layer => fg.addLayer(layer));
  }

  function toggleEditMode() {
    if (!map || state.editing === props.editing) return;

    const mapContainer = map?.getContainer();

    props.editing ? mapContainer.classList.add('editing') : mapContainer.classList.remove('editing');
  }

  function handleColorChange() {
    if (!featureGroupRef.current) return;

    if (state.color !== props.color) {
      featureGroupRef.current.eachLayer(layer => {
        if (typeof layer.setStyle === 'function') {
          layer.setStyle({ color: props.color, fillColor: props.color, zIndex: 9999 });
        }
      });

      setState(prevState => ({ ...prevState, color: props.color }));
    }
  }

  function handleCreateShape(e) {
    setState(prevState => ({ ...prevState, enableDraw: false }));

    const layers = L.layerGroup().addLayer(e.layer);

    setState(prevState => ({ ...prevState, leaflet_id: e.layer._leaflet_id }));

    layers.eachLayer(layer => {
      if (props.type === TYPE.FARM && props.onCreateGeofence) {
        const geoJson = layer.toGeoJSON();
        geoJson.properties.radius = layer._mRadius ? layer._mRadius : layer._radius;

        props.onCreateGeofence(geoJson);
      }
    });

    if (props.isNew) {
      setState(prevState => ({ ...prevState, enableDraw: true }));
    }

    props.setSubmittable(true);
  }

  function handleEditShape(layerGroup) {
    layerGroup.layers.eachLayer(layer => {
      if (props.editFence && props.type === TYPE.FARM) {
        const geoJson = layer.toGeoJSON();

        if (layer._mRadius) {
          geoJson.properties.radius = layer._mRadius;
        } else if (layer._radius) {
          geoJson.properties.radius = layer._radius;
        }

        props.editFence(geoJson);
      }
    });

    setState(prevState => ({ ...prevState, enableDraw: false }));
  }

  function handleDeleteShape() {
    setState(prevState => ({ ...prevState, enableDraw: true }));

    if (props.handleDelete) props.handleDelete();
  }

  async function searchPositionByAddress() {
    const { defaultPosition, farm } = props;
    const query = defaultPosition || farm?.address?.full_address;

    if (!query) return;

    try {
      const addressResults = await provider.search({ query });

      if (!Array.isArray(addressResults) || !addressResults.length) return;

      const { y: lat, x: lng } = addressResults[0] || {};
      const newPosition = [Number(lat), Number(lng)];

      if (isValidPosition(newPosition)) {
        map?.setView(newPosition, 12);

        return newPosition;
      }
    } catch (error) {
      console.error('Error searching position by address:', error);
    }
  }

  function calculateBoundsForCircle(geoJson) {
    if (isEmpty(geoJson?.geometry)) return;

    let bounds = {};
    const { coordinates } = geoJson.geometry;
    const radius = geoJson.properties?.radius || 0;

    if (coordinates?.length === 2 && radius > 0) {
      const [lng, lat] = coordinates;
      const center = L.latLng(lat, lng);

      bounds = center.toBounds(radius * 2);

      return bounds;
    }
  }

  function calculateBoundsForPolygon(geoJson) {
    const group = new L.geoJson(geoJson);
    let bounds = group.getBounds();

    if (bounds.isValid()) {
      const southWest = bounds.getSouthWest();
      const northEast = bounds.getNorthEast();

      const corner1 = L.latLng(parseFloat(northEast.lat), parseFloat(northEast.lng));
      const corner2 = L.latLng(parseFloat(southWest.lat), parseFloat(southWest.lng));

      bounds = L.latLngBounds(corner1, corner2);

      return bounds;
    }
  }

  function calculateMapBounds(geoData) {
    const source = geoData?.geometry ? geoData : geoData?.features?.at(0);

    if (!source) return;

    const bounds =
      source.geometry?.type === GEOMETRY_TYPE.POINT && source.properties?.radius
        ? calculateBoundsForCircle(source)
        : calculateBoundsForPolygon(source);

    setState(prevState => ({ ...prevState, farm: props.farm }));

    if (map) {
      const center = bounds.getCenter();
      map?.setView(center, Math.min(map?.getBoundsZoom(bounds), ZOOM_MAX));
    }
  }

  const { farms, geofences, sites } = useFeatures({
    mapData: { farms: [props.farm?.master_geofence], geofences: props.farm?.geofences, sites: props.farm?.sites }
  });

  async function tick() {
    if (!props?.farm?.master_geofence) return;

    setState(prevState => ({
      ...prevState,
      masterGeofences: { ...farms, features: farms.features.filter(f => f.properties?.id !== props.farm?.id) },
      geofences: { ...geofences, features: geofences.features.filter(f => !f.properties?.is_master) },
      sites
    }));

    await calculateMapBounds(props?.farm?.master_geofence?.geo_json);
  }

  /**
   * Not used, remained to keep the structure and if we change the behavior
   */
  function handleSave() {
    props.handleSaveEditing();
    exitFullScreenMode();
  }

  function exitFullScreenMode() {
    if (map?.fullscreenControl) {
      document.exitFullscreen();
    }
  }

  return (
    <LayersControl position={LAYERS_CONTROL_POSITION}>
      <BaseLayers />

      {!props.isNew && (
        <CustomLayers
          features={state.geofences}
          features_master={state.farms}
          features_sites={state.sites}
          grazing={props.grazing}
        />
      )}

      <ConditionalWrapper
        condition={!props.isNew}
        wrapper={children => (
          <Overlay checked name="Current Farm">
            {children}
          </Overlay>
        )}>
        <LayerGroup>
          <FeatureGroup ref={featureGroupRef}>
            <EditControl
              draw={{
                ...EDIT_CONTROL_DRAW_OPTIONS,
                circle: props.editing &&
                  state.enableDraw && {
                    shapeOptions: { ...SHAPE_OPTIONS, color: state.color, fillColor: state.color }
                  },
                polygon: props.editing &&
                  state.enableDraw && {
                    shapeOptions: { ...SHAPE_OPTIONS, color: state.color, fillColor: state.color }
                  }
              }}
              edit={{ featureGroup: featureGroupRef?.current, edit: true, remove: true }}
              position={EDIT_CONTROL_POSITION}
              onEdited={handleEditShape}
              onCreated={handleCreateShape}
              onDeleted={handleDeleteShape}
            />
          </FeatureGroup>
        </LayerGroup>

        {isFullScreen && props.editing && (
          <EditButtons handleCancel={props.handleCancelEditing} handleReturn={exitFullScreenMode} />
        )}
      </ConditionalWrapper>
    </LayersControl>
  );
}

function Map(props) {
  return (
    <AppMapContainer
      height={400}
      className={classnames('map', { 'edit-controls-visible': props.isNew })}
      loading={props.loading}>
      <FarmLayer {...props} />
    </AppMapContainer>
  );
}

export default memo(Map);
